// Space + people config for the Phase B multi-space portal (hub, switcher,
// per-space shells). Identity hues live in portalSpaces.css as portal-scoped
// --space-* / --person-* tokens; this module references them by var() name so
// the colors stay theme-aware (Comfort-Dark contrast nudges happen in CSS, not
// here). Identity glyphs are Sharp Duotone Solid; generic chrome icons stay
// Sharp Solid to match the rest of the portal.
//
// The reachable-space list mirrors the Phase A Firestore wall: owners
// (users/{uid}.owner === true — Anthony, Grace) reach all four spaces, everyone
// else reaches only Work. The UI is courtesy; the rules are the guard.

export const PEOPLE = {
	anthony: { initials: 'A', name: 'Anthony', color: 'var(--person-anthony)' },
	grace: { initials: 'G', name: 'Grace', color: 'var(--person-grace)' },
	annelise: { initials: 'An', name: 'Annelise', color: 'var(--person-annelise)' },
};

export const SPACES = {
	work: {
		key: 'work',
		name: 'Work',
		route: '/portal/work',
		icon: 'fa-people-group',
		sub: 'Tasks · Projects · Students · Leads',
		privacy: 'Shared',
		members: ['anthony', 'grace'],
		summary: { zero: 'All clear' },
		// The hero slot is the Phase C mount point.
		heroSlot: {
			name: 'Tasks',
			icon: 'fa-list-check',
			phase: 'Phase C',
			big: true,
			primary: true,
			hint: 'Your task list & board live here',
			sub: 'Needs-action, board, projects — added next.',
			cta: 'Add your first task',
			ctaIcon: 'fa-plus',
		},
		// Reserved, named-but-empty slots the space grows into.
		reservedSlots: [
			{ name: 'Projects', icon: 'fa-folder-tree' },
			{ name: 'Students', icon: 'fa-graduation-cap' },
			{ name: 'Leads', icon: 'fa-user-plus' },
		],
	},
	personal: {
		key: 'personal',
		name: 'Personal',
		route: '/portal/personal',
		icon: 'fa-house-chimney',
		// Describes what the space actually renders — keep in step with the body.
		sub: 'Tasks · Subscriptions · Capture inbox',
		privacy: 'Private',
		members: ['anthony', 'grace'],
		summary: { zero: 'Nothing due' },
		// LIVE: this space reads real Firestore data rather than static slot copy,
		// and its BODY is the task machinery bound to `personalTasks` (see
		// SPACE_TASK_SERVICES in SpaceShell). Between the tasks and the Capture
		// slot sits the Subscriptions cut ledger (SPACE_PANELS, bound to
		// `personalSubscriptions`). The only slot left is Capture, built by
		// spaceItemsModel.buildCaptureSlots() — the one thing Owners and Barbara
		// don't have.
		//
		// `personalItems` is deliberately no longer read: Personal's tasks moved
		// to the real machinery and Anthony chose to start it empty rather than
		// migrate the ten legacy documents. They still exist in Firestore.
		live: { captures: 'captureItems' },
	},
	owners: {
		key: 'owners',
		name: 'Owners',
		route: '/portal/owners',
		icon: 'fa-building-columns',
		sub: 'Pay · Payroll · Taxes',
		privacy: 'Private',
		members: ['anthony', 'grace'],
		summary: { zero: 'Nothing open' },
		// Runs the real task machinery (bound to `ownersTasks` in SpaceShell), so
		// its Phase B design-copy slots are superseded and deliberately gone.
	},

	barbara: {
		key: 'barbara',
		name: 'Barbara',
		route: '/portal/barbara',
		icon: 'fa-hand-holding-heart',
		sub: 'Bills · Appointments · Statements',
		privacy: 'Private',
		members: ['anthony', 'grace'],
		summary: { zero: 'Nothing due' },
		// Runs the real task machinery (bound to `barbaraTasks` in SpaceShell), so
		// its Phase B design-copy slots are superseded and deliberately gone.
	},

	// Anthony's alone, NOT the owners' (Anthony, 2026-10-18): Grace does not
	// reach it. The body is a read-only browser over `badBibleTabs`, a copy of
	// his Bad Bible Google Sheet written by scripts/seedBadBible.js.
	bible: {
		key: 'bible',
		name: 'Bad Bible',
		route: '/portal/bible',
		icon: 'fa-book-bible',
		sub: 'Passages · Contradictions · Prophecies',
		privacy: 'Only you',
		members: ['anthony'],
		summary: { zero: 'Reference' },
		onlyEmail: 'anthony@coloryourvoice.com',
	},
};

export const SPACE_ORDER = ['work', 'personal', 'owners', 'barbara', 'bible'];

// The wall, mirrored client-side: owners reach the shared private spaces;
// everyone else reaches only Work. A space with `onlyEmail` is further limited
// to that one signed-in account, matching its rule in firestore.rules. Returns
// ordered space keys.
export function getReachableSpaceKeys(userProfile, email) {
	if (userProfile?.owner !== true) return ['work'];
	return SPACE_ORDER.filter((k) => {
		const only = SPACES[k].onlyEmail;
		return !only || (email || '').toLowerCase() === only;
	});
}

export function getReachableSpaces(userProfile, email) {
	return getReachableSpaceKeys(userProfile, email).map((k) => SPACES[k]);
}
